import React, { useState } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramBoilerFireRate } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.hydronicBoilerFireRate.v1';

type Fields = { output: string; hhv: string; fireRate: string };
const EMPTY: Fields = { output: '', hhv: '', fireRate: '' };

/**
 * Boiler Fire Rate — Fire Rate = Output ÷ HHV
 * Bidirectional: fill any 2 of 3 fields.
 */
const HydronicBoilerFireRateScreen = () => {
  const { unit, toImp, fromImp } = useUnits();
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);

  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  const calculate = () => {
    setError(null);
    const filled = (Object.values(fields) as string[]).filter((v) => v !== '').length;
    if (filled < 2) { setError('Fill any 2 of the 3 fields to solve for the missing one.'); return; }
    if (filled === 3) { setError('All 3 values are filled — clear one to solve for it.'); return; }

    const out = toImp(fields.output, 'heatRate');
    const hhv = parseFloat(fields.hhv);
    const fr = parseFloat(fields.fireRate);

    if (fields.fireRate === '') {
      if (!(out > 0)) { setError('Boiler output must be greater than zero.'); return; }
      if (!(hhv > 0)) { setError('HHV must be greater than zero.'); return; }
      setSolvedFor('fireRate');
      set({ fireRate: parseFloat((out / hhv).toFixed(4)).toString() });
    } else if (fields.output === '') {
      if (!(hhv > 0)) { setError('HHV must be greater than zero.'); return; }
      if (!(fr > 0)) { setError('Fire rate must be greater than zero.'); return; }
      setSolvedFor('output');
      set({ output: fromImp(fr * hhv, 'heatRate', 2) });
    } else if (fields.hhv === '') {
      if (!(out > 0)) { setError('Boiler output must be greater than zero.'); return; }
      if (!(fr > 0)) { setError('Fire rate must be greater than zero.'); return; }
      setSolvedFor('hhv');
      set({ hhv: parseFloat((out / fr).toFixed(2)).toString() });
    }
  };

  const clearAll = () => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  };

  const result = [
    fields.output && { label: 'Boiler Output', value: fields.output, unit: unit('heatRate'), emphasis: solvedFor === 'output' },
    fields.hhv && { label: 'HHV', value: fields.hhv, unit: 'BTU/lb or BTU/gal', emphasis: solvedFor === 'hhv' },
    fields.fireRate && { label: 'Fire Rate', value: fields.fireRate, unit: 'lbs/h or gal/h', emphasis: solvedFor === 'fireRate' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[];

  return (
    <CalcShell
      title="Boiler Fire Rate"
      subtitle="Solve for fire rate, output, or HHV using Fire Rate = Output ÷ HHV"
      formula={
        <>
          <FormulaLine>Fire Rate = Output ÷ HHV</FormulaLine>
          <FormulaNote>Output = Boiler Output ({unit('heatRate')}), HHV = Higher Heating Value (BTU/lb or BTU/gal)</FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter="Formula: Fire Rate = Output ÷ HHV"
      diagram={<DiagramBoilerFireRate />}
      outputSlots={{
        boiler_output_btuh: fields.output ? toImp(fields.output, 'heatRate') : undefined,
        boiler_hhv: fields.hhv ? parseFloat(fields.hhv) : undefined,
        boiler_fire_rate_gph: fields.fireRate ? parseFloat(fields.fireRate) : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.boiler_output_btuh != null && !fields.output) {
          patch.output = fromImp(values.boiler_output_btuh, 'heatRate', 2);
        }
        if (values.boiler_hhv != null && !fields.hhv) {
          patch.hhv = parseFloat(Number(values.boiler_hhv).toFixed(2)).toString();
        }
        if (values.boiler_fire_rate_gph != null && !fields.fireRate) {
          patch.fireRate = parseFloat(Number(values.boiler_fire_rate_gph).toFixed(4)).toString();
        }
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Boiler Output" value={fields.output} onChangeText={(v) => set({ output: v })} rightText={unit('heatRate')} maxDecimals={2} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="HHV" value={fields.hhv} onChangeText={(v) => set({ hhv: v })} rightText="BTU/lb" maxDecimals={2} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Fire Rate" value={fields.fireRate} onChangeText={(v) => set({ fireRate: v })} rightText="lbs/h or gal/h" maxDecimals={4} onSubmitEditing={calculate} />
      </View>
    </CalcShell>
  );
};

export default HydronicBoilerFireRateScreen;
